import React, { useState, useEffect } from 'react';
import '../styles/UserManagement.css';
import { userService } from '../services/userService';

const UserManagement = ({ authToken }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // NEW: Available locations from database
  const [availableLocations, setAvailableLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // NEW: Selected locations for multi-select
  const [selectedLocations, setSelectedLocations] = useState([]);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    query_cap: 10,
    allow_querying: 'Yes',
    location_access: 'ALL',
    is_admin: 'No',
    thread_warn_at: 10,
    thread_max_messages: 15,
    skill_access: 'No'
  });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchAvailableLocations(); // NEW: Fetch locations on mount
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await userService.getAllUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Fetch all available locations from backend
  const fetchAvailableLocations = async () => {
    setLoadingLocations(true);
    try {
      const locations = await userService.getAvailableLocations();
      setAvailableLocations(locations);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      setAvailableLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = async (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      password: '',
      query_cap: user.query_cap || 10,
      allow_querying: user.allow_querying || 'Yes',
      location_access: user.location_access || 'ALL',
      is_admin: user.is_admin || 'No',
      thread_warn_at: user.thread_warn_at || 10,
      thread_max_messages: user.thread_max_messages || 15,
      skill_access: user.skill_access || 'No'
    });

    // NEW: Fetch user's current location mappings if not ALL
    if (user.location_access !== 'ALL') {
      try {
        const userLocations = await userService.getUserLocations(user.username);
        setSelectedLocations(userLocations);
      } catch (error) {
        console.error('Failed to fetch user locations:', error);
        setSelectedLocations([]);
      }
    } else {
      setSelectedLocations([]);
    }

    setFormError('');
    setFormSuccess('');
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      query_cap: 10,
      allow_querying: 'Yes',
      location_access: 'ALL',
      is_admin: 'No',
      thread_warn_at: 10,
      thread_max_messages: 15,
      skill_access: 'No'
    });
    setSelectedLocations([]); // NEW: Reset selected locations
    setFormError('');
    setFormSuccess('');
    setShowModal(true);
  };

  const handleDelete = async (username) => {
    if (window.confirm(`Are you sure you want to delete user ${username}?`)) {
      try {
        const result = await userService.deleteUser(username);
        if (result.success) {
          fetchUsers();
        } else {
          alert(`Failed to delete user: ${result.error || 'Unknown error'}`);
        }
      } catch (error) {
        alert(`Failed to delete user: ${error.message}`);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // NEW: If location_access changes to ALL, clear selected locations
    if (name === 'location_access' && value === 'ALL') {
      setSelectedLocations([]);
    }
  };

  // NEW: Handle location checkbox toggle
  const handleLocationToggle = (location) => {
    setSelectedLocations(prev => {
      if (prev.includes(location)) {
        // Remove location
        return prev.filter(loc => loc !== location);
      } else {
        // Add location
        return [...prev, location];
      }
    });
  };

  // NEW: Select/Deselect all locations
  const handleSelectAllLocations = () => {
    if (selectedLocations.length === availableLocations.length) {
      setSelectedLocations([]);
    } else {
      setSelectedLocations([...availableLocations]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    // Validation
    if (!formData.email || !formData.username) {
      setFormError('Email and username are required');
      return;
    }

    if (!editingUser && !formData.password) {
      setFormError('Password is required for new users');
      return;
    }

    // NEW: Validate location selection if not ALL
    if (formData.location_access !== 'ALL' && selectedLocations.length === 0) {
      setFormError('Please select at least one location or choose "ALL"');
      return;
    }

    try {
      let result;

      // Prepare user data for backend
      const userData = {
        ...formData,
        location_access: formData.location_access // Set to 'ALL' or NULL
      };

      if (editingUser) {
        // Update existing user
        result = await userService.updateUser(editingUser.username, userData);
      } else {
        // Create new user
        result = await userService.createUser(userData);
      }

      if (result.success) {
        // NEW: If location_access is not ALL, update location mappings
        if (formData.location_access !== 'ALL') {
          const locationResult = await userService.updateUserLocations(
            editingUser ? editingUser.username : formData.username,
            selectedLocations
          );

          if (!locationResult.success) {
            setFormError('User saved but failed to update locations: ' + locationResult.error);
            return;
          }
        }

        setFormSuccess(editingUser ? 'User updated successfully!' : 'User created successfully!');
        setTimeout(() => {
          setShowModal(false);
          fetchUsers();
        }, 1500);
      } else {
        setFormError(result.error || 'Operation failed');
      }
    } catch (error) {
      setFormError(error.message || 'An error occurred');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setSelectedLocations([]); // NEW: Reset locations
    setFormError('');
    setFormSuccess('');
  };

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h1 className="header-title">User Management</h1>
        <p className="header-subtitle">Manage users, permissions, and location access</p>
      </div>

      <div className="user-toolbar">
        <div className="search-box">
          <svg className="search-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" />
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="add-user-button" onClick={handleAdd}>
          <span>➕</span>
          <span>Add User</span>
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Username</th>
                <th>Role</th>
                <th>Query Cap</th>
                <th>Thread Warn</th>
                <th>Thread Max</th>
                <th>Location Access</th>
                <th>Skills</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map(user => (
                  <tr key={user.username}>
                    <td className="user-email">{user.email}</td>
                    <td>{user.username}</td>
                    <td>
                      <span className={`user-badge ${user.is_admin === 'Yes' ? 'badge-admin' : 'badge-user'}`}>
                        {user.is_admin === 'Yes' ? 'ADMIN' : 'USER'}
                      </span>
                    </td>
                    <td>{user.query_cap}</td>
                    <td>{user.thread_warn_at || 10}</td>
                    <td>{user.thread_max_messages || 15}</td>
                    <td>
                      <span className={`user-badge ${user.location_access === 'ALL' ? 'badge-all-access' : 'badge-restricted'}`}>
                        {user.location_access === 'ALL' ? 'ALL LOCATIONS' : 'RESTRICTED'}
                      </span>
                    </td>
                    <td>
                      <span className={`user-badge ${user.skill_access === 'Yes' ? 'badge-all-access' : 'badge-restricted'}`}>
                        {user.skill_access === 'Yes' ? '⚡ YES' : 'NO'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="action-button edit-btn"
                          onClick={() => handleEdit(user)}
                          title="Edit User"
                        >
                          ✏️
                        </button>
                        <button
                          className="action-button delete-btn"
                          onClick={() => handleDelete(user.username)}
                          title="Delete User"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal for Add/Edit User */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="user-form">
              {formError && (
                <div className="form-alert alert-error">
                  <span>⚠️</span>
                  <span>{formError}</span>
                </div>
              )}

              {formSuccess && (
                <div className="form-alert alert-success">
                  <span>✓</span>
                  <span>{formSuccess}</span>
                </div>
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={editingUser !== null}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="username">Username *</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    disabled={editingUser !== null}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    Password {!editingUser && '*'}
                    {editingUser && ' (leave blank to keep current)'}
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!editingUser}
                    className="form-input"
                    placeholder={editingUser ? 'Leave blank to keep current password' : ''}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="query_cap">Query Cap</label>
                  <input
                    type="number"
                    id="query_cap"
                    name="query_cap"
                    value={formData.query_cap}
                    onChange={handleInputChange}
                    min="0"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="thread_warn_at">Thread Warning At</label>
                  <input
                    type="number"
                    id="thread_warn_at"
                    name="thread_warn_at"
                    value={formData.thread_warn_at}
                    onChange={handleInputChange}
                    min="1"
                    className="form-input"
                    title="Show warning after this many messages in a thread"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="thread_max_messages">Thread Max Messages</label>
                  <input
                    type="number"
                    id="thread_max_messages"
                    name="thread_max_messages"
                    value={formData.thread_max_messages}
                    onChange={handleInputChange}
                    min="1"
                    className="form-input"
                    title="Block new messages after this limit"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="is_admin">Role</label>
                  <select
                    id="is_admin"
                    name="is_admin"
                    value={formData.is_admin}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="No">User</option>
                    <option value="Yes">Admin</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="allow_querying">Allow Querying</label>
                  <select
                    id="allow_querying"
                    name="allow_querying"
                    value={formData.allow_querying}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="skill_access">Skill Access</label>
                  <select
                    id="skill_access"
                    name="skill_access"
                    value={formData.skill_access}
                    onChange={handleInputChange}
                    className="form-input"
                    title="Allow user to create and use AI Skills"
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                {/* NEW: Location Access Type Selector */}
                <div className="form-group full-width">
                  <label htmlFor="location_access">Location Access Type</label>
                  <select
                    id="location_access"
                    name="location_access"
                    value={formData.location_access}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="ALL">ALL - Unrestricted access to all locations</option>
                    <option value="RESTRICTED">RESTRICTED - Specific locations only</option>
                  </select>
                </div>

                {/* NEW: Location Multi-Select (only show if RESTRICTED) */}
                {formData.location_access === 'RESTRICTED' && (
                  <div className="form-group full-width">
                    <div className="location-selector-header">
                      <label>Select Accessible Locations *</label>
                      <button
                        type="button"
                        className="select-all-btn"
                        onClick={handleSelectAllLocations}
                      >
                        {selectedLocations.length === availableLocations.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>

                    {loadingLocations ? (
                      <div className="loading-locations">Loading locations...</div>
                    ) : availableLocations.length === 0 ? (
                      <div className="no-locations">No locations available</div>
                    ) : (
                      <div className="location-grid">
                        {availableLocations.map(location => (
                          <label key={location} className="location-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedLocations.includes(location)}
                              onChange={() => handleLocationToggle(location)}
                            />
                            <span className="location-label">{location}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    <div className="selected-count">
                      Selected: {selectedLocations.length} location(s)
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
